import { NavLink } from 'react-router-dom';
import { Users, LogOut, Menu, X, ChevronRight } from 'lucide-react';
import { useAppSidebar } from '../../hooks/sidebar/useAppSidebar';
import { useSidebar } from '../../hooks/sidebar/useMobileSidebar';
import { OrdersRecentFlyout } from './OrdersRecentFlyout';
import logo from '../../assets/bmp-logo-white.png';
import './AppSidebar.scss';

type UserType = 'ORG_OWNER' | 'ORG_ADMIN' | 'ORG_USER' | 'USER' | 'SUBUSER';

export function AppSidebar() {
  const {
    navigationItems,
    displayName,
    displayEmail,
    userLoading,
    isActive,
    handleSignOut,
    userType,
  } = useAppSidebar();

  const {
    isMobileMenuOpen,
    isMobile,
    toggleMobileMenu,
    closeMobileMenu,
    isDesktopCollapsed,
    toggleDesktopSidebar,
  } = useSidebar();

  const navTo = (['ORG_OWNER', 'ORG_ADMIN', 'ORG_USER'] as UserType[]).includes(userType)
    ? '/orders'
    : '/customer-products';

  return (
    <>
      {/* Mobile Menu Toggle Button */}
      {isMobile && (
        <button className="mobile-menu-toggle" onClick={toggleMobileMenu} aria-label="Toggle menu">
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      )}

      {/* Overlay for mobile */}
      {isMobile && isMobileMenuOpen && (
        <div className="sidebar-overlay" onClick={closeMobileMenu} />
      )}

      {/* Desktop pull-tab — visible only when sidebar is collapsed */}
      {!isMobile && isDesktopCollapsed && (
        <button
          className="sidebar-pull-tab"
          onClick={toggleDesktopSidebar}
          title="Open sidebar"
          aria-label="Open sidebar"
        >
          <ChevronRight size={14} />
        </button>
      )}

      {/* Sidebar */}
      <aside
        className={`sidebar ${isMobileMenuOpen ? 'sidebar--open' : ''} ${
          !isMobile && isDesktopCollapsed ? 'sidebar--collapsed' : ''
        }`}
      >
        {/* Header */}
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <NavLink to={navTo} className="sidebar-brand">
              <img src={logo} alt="DeFeo Fleet" className="sidebar-brand__logo" />
            </NavLink>
          </div>
          {/* Desktop collapse button */}
          {!isMobile && (
            <button
              className="sidebar-close-btn"
              onClick={toggleDesktopSidebar}
              title="Hide sidebar"
              aria-label="Hide sidebar"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          <p className="nav-label">Navigation</p>
          <ul>
            {navigationItems.map((item) => {
              const disabled = !!item.disabled;
              const active = isActive(item.url);

              const content = (
                <>
                  <item.icon size={18} className="nav-icon" />
                  <div className="nav-text">
                    <div className="nav-text__top">
                      <span>{item.title}</span>
                      {!!item.badgeCount && (
                        <span
                          className={`nav-badge${
                            item.badgeTone === 'attention' ? ' nav-badge--attention' : ''
                          }`}
                          aria-label={`${item.badgeCount} awaiting action`}
                        >
                          {item.badgeCount > 99 ? '99+' : item.badgeCount}
                        </span>
                      )}
                    </div>
                    <small>{item.description}</small>
                  </div>
                </>
              );

              const link = disabled ? (
                <div
                  className="nav-item disabled"
                  aria-disabled="true"
                  tabIndex={-1}
                  onClick={(e) => e.preventDefault()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') e.preventDefault();
                  }}
                  title="Disabled - Configure organization and warehouses"
                >
                  {content}
                </div>
              ) : (
                <NavLink
                  to={item.url}
                  className={`nav-item ${active ? 'active' : ''}`}
                  title={item.description}
                  onClick={isMobile ? closeMobileMenu : undefined}
                >
                  {content}
                </NavLink>
              );

              // The Orders item gets a hover flyout of the loads this user
              // recently edited — org staff on a real pointer only.
              const withOrdersFlyout =
                item.title === 'Orders' &&
                !disabled &&
                !isMobile &&
                (['ORG_OWNER', 'ORG_ADMIN', 'ORG_USER'] as UserType[]).includes(userType);

              if (withOrdersFlyout) {
                return (
                  <OrdersRecentFlyout key={item.title} ordersUrl={item.url}>
                    {link}
                  </OrdersRecentFlyout>
                );
              }

              return <li key={item.title}>{link}</li>;
            })}
          </ul>
        </nav>

        {/* Footer */}
        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="sidebar-avatar">
              <Users size={14} />
            </div>
            <div>
              <p className="user-name">{userLoading ? 'Loading…' : displayName}</p>
              <p className="user-email">{userLoading ? '…' : displayEmail}</p>
            </div>
          </div>

          <button className="btn btn--ghost btn--sm sidebar-signout" onClick={handleSignOut}>
            <LogOut size={16} style={{ marginRight: 6 }} />
            Sign out
          </button>
        </div>
      </aside>
    </>
  );
}
